import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { exportNotesCSV, exportNotesPDF } from '../utils/export.helpers';
import { getUser } from '../utils/auth.helpers';

// Rôles autorisés à saisir après la deadline
const PRIVILEGED_ROLES = ['ADMIN_ECOLE', 'DIRECTEUR', 'SUPER_ADMIN'];

// Délai de saisie en jours après la date d'évaluation (configurable)
const SAISIE_DEADLINE_DAYS = 7;

function isDeadlinePassed(evalDate) {
  if (!evalDate) return false;
  const deadline = new Date(evalDate);
  deadline.setDate(deadline.getDate() + SAISIE_DEADLINE_DAYS);
  return new Date() > deadline;
}

function getAppreciation(note, bareme) {
  if (note === '' || note == null) return '';
  const pct = (Number(note) / bareme) * 20;
  if (pct >= 18) return 'Excellent';
  if (pct >= 16) return 'Très bien';
  if (pct >= 14) return 'Bien';
  if (pct >= 12) return 'Assez bien';
  if (pct >= 10) return 'Passable';
  if (pct >= 8)  return 'Insuffisant';
  return 'Très insuffisant';
}

export default function EvaluationEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const user = getUser();
  const userRoles = user?.roles || [user?.role] || [];
  const isPrivileged = userRoles.some((r) => PRIVILEGED_ROLES.includes(r));

  useEffect(() => {
    async function load() {
      try {
        const evaluation = await api.get(`/evaluations/${id}`);
        setEv(evaluation);
        const students = await api.get('/students');
        const roster = students.filter((s) =>
          (s.enrollments || []).some((en) => en.classId === evaluation.classId && en.status === 'ACTIVE'),
        );
        const existing = new Map((evaluation.results || []).map((r) => [r.studentId, r]));
        setRows(roster.map((s) => {
          const r = existing.get(s.id);
          const note = r && r.totalObtained != null ? String(r.totalObtained) : '';
          return {
            studentId: s.id,
            matricule: s.matricule,
            name: `${s.lastName} ${s.firstName}`,
            note,
            absent: r ? r.absent : false,
            appreciation: r?.appreciation || getAppreciation(note, evaluation.baremeTotal ?? 20),
          };
        }));
      } catch (err) {
        setError(err.message);
      }
    }
    load();
  }, [id]);

  const bareme = ev?.baremeTotal ?? 20;
  const deadlinePassed = isDeadlinePassed(ev?.date);
  const locked = ev?.status === 'CLOTURE' || (deadlinePassed && !isPrivileged);
  const lockReason = ev?.status === 'CLOTURE'
    ? 'Évaluation clôturée.'
    : deadlinePassed && !isPrivileged
      ? `Délai de saisie dépassé (${SAISIE_DEADLINE_DAYS}j). Contactez un administrateur.`
      : null;

  function update(studentId, patch) {
    setSaved(false);
    setRows((rs) => rs.map((r) => {
      if (r.studentId !== studentId) return r;
      const updated = { ...r, ...patch };
      // Auto-appréciation quand la note change
      if ('note' in patch && !updated.absent) {
        updated.appreciation = getAppreciation(patch.note, bareme);
      }
      // Quand absent cochée, vider la note
      if ('absent' in patch && patch.absent) {
        updated.note = '';
        updated.appreciation = 'Absent';
      }
      if ('absent' in patch && !patch.absent) {
        updated.appreciation = '';
      }
      return updated;
    }));
  }

  const invalid = useMemo(() =>
    rows.some((r) => !r.absent && r.note !== '' &&
      (Number(r.note) < 0 || Number(r.note) > bareme || Number.isNaN(Number(r.note)))
    ), [rows, bareme]
  );

  const stats = useMemo(() => {
    const noted = rows.filter((r) => !r.absent && r.note !== '');
    if (!noted.length) return null;
    const notes = noted.map((r) => Number(r.note));
    return {
      moy: (notes.reduce((a, b) => a + b, 0) / notes.length).toFixed(2),
      max: Math.max(...notes),
      min: Math.min(...notes),
      absents: rows.filter((r) => r.absent).length,
    };
  }, [rows]);

  async function save() {
    setError(''); setSaving(true);
    try {
      const items = rows.map((r) => ({
        studentId: r.studentId,
        absent: r.absent,
        totalObtained: r.absent || r.note === '' ? undefined : Number(r.note),
        appreciation: r.appreciation || undefined,
      }));
      await api.post(`/evaluations/${id}/results/bulk`, { items });
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (error && !ev) return <div className="form-error">{error}</div>;
  if (!ev) return <div className="loading">Chargement…</div>;

  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <Link to={`/evaluations/${id}`} className="back-link" style={{ marginBottom: 4 }}>← {ev.title}</Link>
          <h2>Saisie des notes · barème /{bareme}</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Indicateur deadline */}
          {deadlinePassed && isPrivileged && (
            <span className="badge gold" style={{ fontSize: '0.72rem' }}>
              ⚠ Délai dépassé — accès admin
            </span>
          )}
          {deadlinePassed && !isPrivileged && (
            <span className="badge red" style={{ fontSize: '0.72rem' }}>
              🔒 Délai de saisie dépassé
            </span>
          )}
          {!deadlinePassed && (
            <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
              Délai : {new Date(new Date(ev.date).setDate(new Date(ev.date).getDate() + SAISIE_DEADLINE_DAYS)).toLocaleDateString('fr-FR')}
            </span>
          )}
          {saved && <span className="badge green">Enregistré ✓</span>}
          <button className="btn-ghost btn-sm" onClick={() => exportNotesCSV(ev, rows)}>↓ Excel</button>
          <button className="btn-ghost btn-sm" onClick={() => exportNotesPDF(ev, rows)}>↓ PDF</button>
          <button className="btn" style={{ width: 'auto', padding: '0 20px', height: 40 }}
            onClick={save} disabled={saving || invalid || locked}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className="panel-body">
        {lockReason && <div className="form-error" style={{ marginBottom: 12 }}>{lockReason}</div>}
        {error && <div className="form-error">{error}</div>}
        {invalid && <div className="form-error">Une note dépasse le barème /{bareme} ou est invalide.</div>}

        {/* Stats en temps réel */}
        {stats && (
          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 12 }}>
            <div className="kpi"><span>Moyenne</span><strong>{stats.moy}/{bareme}</strong></div>
            <div className="kpi green"><span>Max</span><strong>{stats.max}/{bareme}</strong></div>
            <div className="kpi"><span>Min</span><strong>{stats.min}/{bareme}</strong></div>
            <div className="kpi"><span>Absents</span><strong>{stats.absents}</strong></div>
          </div>
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Matricule</th>
                <th>Élève</th>
                <th className="num">Note /{bareme}</th>
                <th style={{ textAlign: 'center' }}>Absent</th>
                <th>Appréciation</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const bad = !r.absent && r.note !== '' && (Number(r.note) < 0 || Number(r.note) > bareme);
                return (
                  <tr key={r.studentId} style={r.absent ? { background: 'var(--surface-soft, #f8f8f8)', opacity: 0.7 } : {}}>
                    <td className="mono">{r.matricule}</td>
                    <td>{r.name}</td>
                    <td className="num">
                      <input
                        className="grid-note"
                        style={{
                          borderColor: bad ? 'var(--danger)' : undefined,
                          background: r.absent ? '#e9e9e9' : undefined,
                          cursor: r.absent ? 'not-allowed' : undefined,
                        }}
                        type="number" step="0.25" min="0" max={bareme}
                        value={r.absent ? '' : r.note}
                        disabled={r.absent || locked}
                        placeholder={r.absent ? 'Absent' : ''}
                        onChange={(e) => update(r.studentId, { note: e.target.value })}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={r.absent} disabled={locked}
                        onChange={(e) => update(r.studentId, { absent: e.target.checked })} />
                    </td>
                    <td>
                      <input className="grid-appr"
                        value={r.appreciation}
                        disabled={locked}
                        placeholder={r.absent ? 'Absent' : '—'}
                        style={{ color: r.absent ? 'var(--muted)' : undefined }}
                        onChange={(e) => update(r.studentId, { appreciation: e.target.value })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <div className="empty">Aucun élève inscrit dans cette classe.</div>}
      </div>
    </div>
  );
}
